import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import axios from 'axios';
import { toast } from 'react-toastify';
import Topbar from '../../layouts/Topbar';
import Sidebar from '../../layouts/Sidebar';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import apiConfig, { API_BASE_URL as NAMED_API_BASE_URL } from '../../../config/api';

const API_BASE_URL =
  typeof apiConfig === 'string'
    ? apiConfig
    : NAMED_API_BASE_URL || 'https://pms.burrayhr.com';

/* ------------------------------------------------------------------ */
/*  Shimmer skeleton                                                   */
/* ------------------------------------------------------------------ */
const Shimmer = ({ width = '100%', height = '1rem', className = '', rounded = '4px' }) => (
  <span
    className={`placeholder placeholder-wave col-12 ${className}`}
    style={{ width, height, minHeight: height, borderRadius: rounded, display: 'block' }}
  />
);

const CardSkeleton = ({ delay = 0 }) => (
  <div className="col-md-6 col-lg-4 d-flex" style={{ animationDelay: `${delay}ms` }}>
    <div className="card flex-fill">
      <div className="card-body placeholder-glow">
        <div className="d-flex align-items-start justify-content-between mb-3">
          <Shimmer width="48px" height="48px" rounded="50%" />
          <div className="d-flex gap-1">
            <Shimmer width="28px" height="28px" rounded="4px" />
            <Shimmer width="28px" height="28px" rounded="4px" />
          </div>
        </div>
        <Shimmer width="70%" height="16px" className="mb-2" />
        <div className="d-flex justify-content-between pt-2 border-top mt-3">
          <Shimmer width="40%" height="14px" />
          <Shimmer width="20%" height="20px" rounded="12px" />
        </div>
      </div>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Staggered reveal                                                   */
/* ------------------------------------------------------------------ */
const AnimatedCard = ({ children, index = 0, className = '' }) => (
  <div className={`stats-card-reveal ${className}`} style={{ animationDelay: `${index * 70}ms` }}>
    {children}
  </div>
);

const HRKRAs = () => {
  const [kras, setKras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedKRA, setSelectedKRA] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([]);

  const fetchKRAs = async () => {
    setLoading(true);
    setError('');
    try {
      const token = Cookies.get('token');
      const response = await axios.get(`${API_BASE_URL}/api/key-result-areas`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      setKras(response.data || []);
    } catch (err) {
      setError('Failed to fetch KRAs.');
      toast.error('Failed to fetch KRAs.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableYears = async () => {
    try {
      const token = Cookies.get('token');
      const response = await axios.get(`${API_BASE_URL}/api/key-result-areas/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const years = Array.from(new Set((Array.isArray(response.data) ? response.data : [])
        .map(k => k?.year)
        .filter(y => y !== undefined && y !== null)))
        .sort((a, b) => a - b);
      setAvailableYears(years);

      if (years.length > 0 && !years.includes(selectedYear)) {
        setSelectedYear(years[years.length - 1]);
      }
    } catch (err) {
      // Ignore
    }
  };

  const handleEdit = (kra) => {
    setSelectedKRA(kra);
    setEditModalOpen(true);
  };

  const handleDelete = async (kra) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete "${kra.name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        const token = Cookies.get('token');
        await axios.delete(`${API_BASE_URL}/api/key-result-areas/${kra.id}/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        toast.success('KRA deleted successfully!');
        fetchKRAs();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to delete KRA.');
      }
    }
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setSelectedKRA(null);
  };

  useEffect(() => {
    fetchKRAs();
  }, [selectedYear]);

  useEffect(() => {
    fetchAvailableYears();
  }, []);

  const animStyles = `
      .stats-card-reveal {
          animation: statsFadeInUp 0.45s ease both;
      }
      @keyframes statsFadeInUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
      }
      .placeholder-glow .placeholder {
          animation: placeholder-glow 1.4s ease-in-out infinite;
      }
      .cc-modal-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.45);
          z-index: 1050;
          display: flex;
          align-items: center;
          justify-content: center;
      }
      .cc-modal {
          background: #fff;
          border-radius: 12px;
          padding: 32px;
          width: 100%;
          max-width: 440px;
          box-shadow: 0 16px 48px rgba(0,0,0,0.18);
          position: relative;
          animation: statsFadeInUp 0.3s ease both;
      }
      .cc-hover-card {
          transition: box-shadow 0.2s ease, transform 0.2s ease;
      }
      .cc-hover-card:hover {
          box-shadow: 0 8px 24px rgba(0,0,0,0.10) !important;
          transform: translateY(-2px);
      }
  `;

  return (
    <>
      <style>{animStyles}</style>
      <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
        <Topbar />
        <Sidebar />

        <div className="page-wrapper">
          <div className="content pb-0">
            {/* Header */}
            <AnimatedCard index={0}>
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
                <div>
                  <h4 className="fw-bold mb-1">Key Result Areas (KRAs)</h4>
                  <p className="text-muted mb-0 fs-13">View and manage all Key Result Areas</p>
                </div>

                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <div className="d-flex align-items-center gap-2">
                    <label className="form-label mb-0 fs-14 text-muted">Year:</label>
                    <select
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                      className="form-select form-select-sm"
                      style={{ width: 'auto' }}
                    >
                      {(availableYears.length ? availableYears : [selectedYear])
                        .sort((a, b) => a - b)
                        .map(y => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                  </div>

                  <Link to="/hr/add-key-result-area" className="btn btn-sm btn-primary d-flex align-items-center gap-1">
                    <i className="ti ti-plus" />
                    <span>Add KRA</span>
                  </Link>

                  <button
                    type="button"
                    className="btn btn-sm btn-outline-white border"
                    onClick={fetchKRAs}
                    disabled={loading}
                    title="Refresh"
                  >
                    <i
                      className="ti ti-refresh me-1"
                      style={{
                        display: 'inline-block',
                        transition: 'transform 0.4s',
                        transform: loading ? 'rotate(360deg)' : 'none',
                      }}
                    />
                    Refresh
                  </button>
                </div>
              </div>
            </AnimatedCard>

            {/* Cards Grid */}
            {loading ? (
              <div className="row g-3 mb-4">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <CardSkeleton key={i} delay={i * 80} />
                ))}
              </div>
            ) : error ? (
              <AnimatedCard index={1}>
                <div className="alert alert-danger d-flex align-items-center gap-2" role="alert">
                  <i className="ti ti-alert-circle fs-20" />
                  <div>
                    {error}{' '}
                    <button
                      className="btn btn-sm btn-outline-danger ms-2"
                      onClick={fetchKRAs}
                    >
                      Retry
                    </button>
                  </div>
                </div>
              </AnimatedCard>
            ) : kras.length === 0 ? (
              <AnimatedCard index={1}>
                <div className="card shadow-sm p-5 text-center text-muted">
                  <i className="ti ti-target-off fs-36 mb-2 text-secondary opacity-50" />
                  <h5 className="fw-bold text-dark mb-1">No KRAs Found</h5>
                  <p className="fs-13 text-muted mb-0">No Key Result Areas found for the selected year.</p>
                </div>
              </AnimatedCard>
            ) : (
              <div className="row g-3 mb-4">
                {kras
                  .filter(kra => (kra.year !== undefined && kra.year !== null ? kra.year === selectedYear : true))
                  .map((kra, idx) => (
                    <div className="col-md-6 col-lg-4 d-flex" key={kra.id || idx}>
                      <AnimatedCard index={idx + 1} className="flex-fill">
                        <div className="card flex-fill cc-hover-card shadow-sm h-100 border">
                          <div className="card-body">
                            <div className="d-flex align-items-start justify-content-between mb-3">
                              <div className="avatar avatar-md bg-primary-subtle text-primary rounded-circle d-flex align-items-center justify-content-center">
                                <i className="ti ti-target fs-20" />
                              </div>

                              <div className="d-flex align-items-center gap-1">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-icon btn-light text-primary"
                                  title="Edit KRA"
                                  onClick={() => handleEdit(kra)}
                                >
                                  <i className="ti ti-edit" />
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-icon btn-light text-danger"
                                  title="Delete KRA"
                                  onClick={() => handleDelete(kra)}
                                >
                                  <i className="ti ti-trash" />
                                </button>
                              </div>
                            </div>

                            <h6 className="fw-bold text-dark mb-2">{kra.name}</h6>
                            <div className="d-flex align-items-center justify-content-between pt-2 border-top">
                              <span className="text-muted fs-13">Weight Percentage</span>
                              <span className="badge bg-primary fs-13 px-2 py-1">{kra.percentage || 0}%</span>
                            </div>
                          </div>
                        </div>
                      </AnimatedCard>
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="footer d-flex align-items-center justify-content-between flex-column flex-sm-row row-gap-2 border-top py-2 px-3">
            <p className="text-dark mb-0">
              2026 © <span className="link-primary">Casoe Technologies</span>, All Rights Reserved
            </p>
          </div>
        </div>

        {/* Edit KRA Modal */}
        {editModalOpen && selectedKRA && (
          <div className="cc-modal-backdrop" onClick={closeEditModal}>
            <div className="cc-modal" onClick={(e) => e.stopPropagation()}>
              {/* Close */}
              <button
                className="btn btn-sm btn-icon btn-outline-white border position-absolute"
                style={{ top: 16, right: 16 }}
                onClick={closeEditModal}
                title="Close"
              >
                <i className="ti ti-x" />
              </button>

              {/* Header */}
              <div className="d-flex align-items-center gap-3 mb-4">
                <div className="avatar avtar-lg bg-primary">
                  <i className="ti ti-target fs-22 text-white" />
                </div>
                <div>
                  <h5 className="fw-bold mb-0">Edit Key Result Area</h5>
                  <p className="text-muted fs-13 mb-0">Update KRA details</p>
                </div>
              </div>

              <EditKRAModal
                API_BASE_URL={API_BASE_URL}
                kra={selectedKRA}
                onClose={closeEditModal}
                onSuccess={() => {
                  closeEditModal();
                  fetchKRAs();
                }}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const EditKRAModal = ({ API_BASE_URL, kra, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: kra.name,
    percentage: kra.percentage.toString()
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = Cookies.get('token');
      await axios.put(`${API_BASE_URL}/api/key-result-areas/${kra.id}/`, {
        name: form.name,
        percentage: parseInt(form.percentage)
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      toast.success('KRA updated successfully!');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update KRA.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-3">
        <label className="form-label fw-medium">
          Name <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          name="name"
          value={form.name}
          onChange={handleChange}
          required
          className="form-control"
          placeholder="Enter KRA name"
          disabled={loading}
        />
      </div>

      <div className="mb-3">
        <label className="form-label fw-medium">
          Percentage (%) <span className="text-danger">*</span>
        </label>
        <input
          type="number"
          name="percentage"
          value={form.percentage}
          onChange={handleChange}
          required
          className="form-control"
          placeholder="Enter percentage (0-100)"
          min="0"
          max="100"
          disabled={loading}
        />
      </div>

      <div className="d-flex gap-2 justify-content-end mt-4">
        <button
          type="button"
          onClick={onClose}
          className="btn btn-light"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={loading || !form.name || !form.percentage}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              Updating...
            </>
          ) : (
            <>
              <i className="ti ti-device-floppy me-1" />
              Save Changes
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default HRKRAs;
